import styles from "./unit-page.module.css";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import routes from "@/routes";

import {
  Flex,
  Spin,
  Typography,
  App,
  Space,
  Card,
  Button,
  Descriptions,
  Modal,
  Badge,
  Switch,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ApartmentOutlined,
  LockTwoTone,
  UnlockTwoTone,
} from "@ant-design/icons";

import PlusIcon from "@/icons/PlusIcon";

import EmployeeProfile from "@/components/cards/EmployeeProfile";
import CustomLink from "@/components/ui/CustomLink";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import BackBtn from "@/components/ui/BackBtn";
import UnitModal from "@/components/modals/UnitModal";
import MemberModal from "@/components/modals/MemberModal";
import RemoveMemberModal from "@/components/modals/RemoveMemberModal";

import { getItem, deleteItem, changeMemberAccess } from "@/api/units";

const { Title, Text } = Typography;

// Страница просмотра конкретной РГ
const UnitDetail = () => {
  const { id } = useParams();
  const unitUuid = id.replace(/^admin-/, ""); // Удаляем префикс admin-
  const navigate = useNavigate();
  const { message } = App.useApp();

  // Данные РГ
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showNewMemberModal, setShowNewMemberModal] = useState(false);
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const [deleteMember, setDeleteMember] = useState(null);
  const [showDeletedMembers, setShowDeletedMembers] = useState(false);

  // initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  // Запрос на сервер для получения данных
  const fetchData = async () => {
    setIsLoading(true);

    try {
      const data = await getItem(unitUuid);

      setData(data);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Обновление данных после редактирования или добавления участника
  const handleUpdate = () => {
    setShowModal(false);
    setShowNewMemberModal(false);
    setShowDeleteMemberModal(false);
    fetchData();
  };

  // Установка/снятие доступа участника к дочерним группам
  const setAccessMember = async (member, checked) => {
    await changeMemberAccess(member.uuid, unitUuid, checked);
  };

  // Удаление РГ
  const handleDelete = () => {
    if (data.members?.length > 0) {
      message.open({
        type: "error",
        content:
          "Нельзя удалить рабочую группу, в которой есть участники. Удалите участников и попробуйте снова.",
        duration: 10,
      });
    } else {
      Modal.confirm({
        title: `Вы уверены, что хотите удалить рабочую группу ${data.name}?`,
        content: "Все вложенные группы будут отсоединены.",
        onOk() {
          handleConfirmDelete();
        },
        okText: "Удалить",
        cancelText: "Отмена",
      });
    }
  };

  // Действительно удаляем РГ после подтверждения
  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: data.uuid });
      if (data.uuid === unitUuid) {
        navigate(routes.administration.units);
      } else {
        fetchData();
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  // Инициализация удаления участника из РГ
  const handleDeleteMember = async (employee) => {
    setDeleteMember(employee);
    setShowDeleteMemberModal(true);
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        data && (
          <Flex vertical gap={10} align="start" className={styles.page}>
            <Breadcrumbs
              items={[
                {
                  title: "Административная панель",
                },
                {
                  title: "Рабочие группы",
                  href: routes.administration.units,
                },
                {
                  title: data?.name,
                },
              ]}
            />

            <BackBtn>{data?.name}</BackBtn>

            <Flex vertical gap={30}>
              <Card
                className={styles.content}
                title={
                  <Title level={4} className="no-margin">
                    Информация о рабочей группе
                  </Title>
                }
                extra={
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => setShowModal(true)}
                  >
                    Редактировать
                  </Button>
                }
              >
                <Descriptions column={1}>
                  <Descriptions.Item label="Название">
                    {data.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Тип">
                    {data.unit_type && (
                      <Space>
                        <span>{data.unit_type.name}</span>
                        <Badge
                          title="Участвует в оргструктуре"
                          count={
                            data.unit_type.is_hierarchical ? (
                              <ApartmentOutlined
                                style={{
                                  color: "var(--color-primary)",
                                }}
                              />
                            ) : (
                              0
                            )
                          }
                        />
                      </Space>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Родительская группа">
                    {data.parent?.uuid && (
                      <CustomLink
                        to={routes.administration.unitDetail(data.parent.uuid)}
                      >
                        {data.parent.name}
                      </CustomLink>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Руководитель">
                    {data.manager && (
                      <EmployeeProfile noAvatar showDismiss {...data.manager} />
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Описание">
                    {data.description}
                  </Descriptions.Item>
                </Descriptions>

                {data.disallow_parent_access ? (
                  <Space style={{ marginTop: 5 }}>
                    <LockTwoTone />
                    Приватная рабочая группа
                  </Space>
                ) : (
                  <Space style={{ marginTop: 5 }}>
                    <UnlockTwoTone />
                    Публичная рабочая группа
                  </Space>
                )}
              </Card>

              <Card
                className={styles.content}
                title={
                  <Flex gap={25} align="center">
                    <Title level={4} className="no-margin">
                      Участники
                    </Title>
                    <Flex
                      gap={10}
                      align="center"
                      style={{ fontSize: 12, fontWeight: 400 }}
                    >
                      <Switch
                        value={showDeletedMembers}
                        onChange={setShowDeletedMembers}
                      />
                      Показывать исключенных
                    </Flex>
                  </Flex>
                }
                extra={
                  <Button
                    type="primary"
                    icon={<PlusIcon />}
                    onClick={() => setShowNewMemberModal(true)}
                  ></Button>
                }
              >
                {!!data.members?.length ||
                (showDeletedMembers && !!data.excludedMembers?.length) ? (
                  <Flex vertical gap={10}>
                    {data.members?.map((member) => (
                      <Flex gap={10} justify="space-between" key={member.uuid}>
                        <EmployeeProfile showDismiss {...member} />

                        <Space style={{ marginLeft: "auto" }}>
                          <Switch
                            defaultValue={member.can_access_child_groups}
                            onChange={(checked) =>
                              setAccessMember(member, checked)
                            }
                          />
                          Доступ к дочерним группам
                        </Space>

                        <Button
                          type="link"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteMember(member)}
                        />
                      </Flex>
                    ))}
                    {showDeletedMembers &&
                      data.excludedMembers?.map((member) => (
                        <EmployeeProfile
                          showDismiss
                          {...member}
                          removed_from_unit
                          key={member.uuid}
                        />
                      ))}
                  </Flex>
                ) : (
                  <Text disabled>Добавьте участников</Text>
                )}
              </Card>

              {data.members?.length > 0 ? (
                <Flex vertical gap={10} align="start">
                  <Text type="secondary">
                    Прежде чем удалить рабочую группу, удалите из нее всех
                    участников.
                  </Text>

                  <Button
                    danger
                    type="default"
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                    disabled={true}
                  >
                    Удалить рабочую группу
                  </Button>
                </Flex>
              ) : (
                <Button
                  danger
                  type="link"
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  style={{ alignSelf: "flex-start" }}
                >
                  Удалить рабочую группу
                </Button>
              )}
            </Flex>

            {showModal && (
              <UnitModal
                data={data}
                visible={showModal}
                onSave={handleUpdate}
                onCancel={() => setShowModal(false)}
              />
            )}
            {showNewMemberModal && (
              <MemberModal
                unit_uuid={data.uuid}
                visible={showNewMemberModal}
                onSave={handleUpdate}
                onCancel={() => setShowNewMemberModal(false)}
              />
            )}
            {showDeleteMemberModal && (
              <RemoveMemberModal
                unit={data}
                employee={deleteMember}
                visible={showDeleteMemberModal}
                onConfirm={handleUpdate}
                onCancel={() => setShowDeleteMemberModal(false)}
              />
            )}
          </Flex>
        )
      )}
    </>
  );
};

export default UnitDetail;
